import React, { useEffect, useState } from 'react';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { login as loginRequest } from '../utils/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, token } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      navigate('/', { replace: true });
    }
  }, [navigate, token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await loginRequest(username, password);
      login(response.access_token);
      navigate('/', { replace: true });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to sign in.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', px: 2, bgcolor: '#f8fafc' }}>
      <Card sx={{ width: '100%', maxWidth: 460, borderRadius: 3, boxShadow: '0 24px 60px rgba(15, 23, 42, 0.12)' }}>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827' }}>
                Welcome back to DictZoom
              </Typography>
              <Typography color="text.secondary">
                Sign in to analyze words, explore reading insights, and continue your vocabulary practice.
              </Typography>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <TextField label="Username" value={username} onChange={(event) => setUsername(event.target.value)} fullWidth required />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  fullWidth
                  required
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                  startIcon={<LockOpenRoundedIcon />}
                  disabled={loading}
                  sx={{ py: 1.5, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
              </Stack>
            </Box>
            <Typography color="text.secondary">
              New here?{' '}
              <Link component={RouterLink} to="/register" underline="hover" sx={{ fontWeight: 700 }}>
                Create an account
              </Link>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
